import { useRef, useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'
import EmojiPicker, { EmojiClickData, EmojiStyle } from 'emoji-picker-react'

import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { MOBILE_DEVICE_WIDTH, MOBILE_WIDTH, SIDE_PANEL_READERS, SIDE_PANEL_THREAD } from 'utils/constants'

import CustomIcon from 'components/icon/CustomIcon'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import { TITLES, TOOLTIPS } from 'locales/locales'
import { useTranslation } from 'react-i18next'

interface IOptionCardProps {
  isMyMessage: boolean
  messageId: string
  isPinned: boolean
  threadId?: string
  isSearch?: boolean
  visibleEmojiPicker: boolean
  jumpId: string
  subsequent?: boolean
  isForwarded?: boolean
  setSelectionMode?: (selectionMode: { isSelectionMode: boolean; selectedMessages: string[] }) => void
  onTogglePinMessage?: (messageId: string, pinned: boolean) => void
  onDeleteMessage?: (messageId: string) => void
  onReactionMessage?: (messageId: string, emoji: string, isAdd: boolean) => void
  onReplyMessage?: (visible: boolean, type: string, params?: { messageId: string }) => void
  onReadMessage?: (visible: boolean, type: string, params?: { messageId: string }) => void
  onJumpMessage?: (messageId: string, threadId?: string) => void
  onToggleVisiblePicker: () => void
  onEditMessage?: () => void
}

const Option = ({
  isMyMessage,
  messageId,
  isPinned,
  threadId,
  isSearch,
  visibleEmojiPicker,
  jumpId,
  subsequent,
  isForwarded,
  setSelectionMode,
  onTogglePinMessage,
  onDeleteMessage,
  onReactionMessage,
  onReplyMessage,
  onJumpMessage,
  onToggleVisiblePicker,
  onEditMessage,
  onReadMessage
}: IOptionCardProps) => {
  const optionRef = useRef<HTMLDivElement>(null)
  const [pickerPosition, setPickerPosition] = useState({ top: 0, right: 0 })
  const { t } = useTranslation()

  const handleEmojiClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    const buttonRect = event.currentTarget.getBoundingClientRect()

    setPickerPosition({
      top: buttonRect.top - 30, // Emoji picker'ın tam üstte açılması için
      right: window.innerWidth - buttonRect.right + 20, // Sağ taraftan hizalama
    })
    onToggleVisiblePicker()
  }, [onToggleVisiblePicker])

  useEffect(() => {
    const scrollableDiv = document.getElementById('scrollableDiv')
    
    if (visibleEmojiPicker && scrollableDiv) {
      scrollableDiv.style.overflow = 'hidden'
    } else if (scrollableDiv) {
      scrollableDiv.style.overflow = 'auto'
    }

    return () => {
      const scrollableDiv = document.getElementById('scrollableDiv')
      if (scrollableDiv) {
        scrollableDiv.style.overflow = 'auto'
      }
    }
  }, [visibleEmojiPicker])


  return (
    <Wrapper ref={optionRef} id="option" subsequent={subsequent}>
      <div>
        <Tooltip
          content={null}
          anchorElement="target"
          openDelay={250}
          parentTitle={true}
          position="bottom"
        >
          <div style={{ display: 'flex', gap: '4px' }}>
            {onReactionMessage && (
              <OptionIcon
                source={icons.emojiIcon}
                onClick={handleEmojiClick}
                title={t(TOOLTIPS.EMOJI_REACTION)}
              />
            )}
            {isMyMessage && onEditMessage && !isPinned && !isSearch && !isForwarded && (
              <OptionIcon source={icons.editIcon} onClick={onEditMessage} title={t(TOOLTIPS.EDIT_MESSAGE)} />
            )}
            {setSelectionMode && (
              <OptionIcon
                source={icons.forwardIcon}
                onClick={() => setSelectionMode?.({ isSelectionMode: true, selectedMessages: [messageId] })}
                title={t(TOOLTIPS.FORWARD_MESSAGE)}
              />
            )}
            {onDeleteMessage && isMyMessage && (
              <OptionIcon
                source={icons.deleteIcon}
                onClick={() => onDeleteMessage(messageId)}
                title={t(TOOLTIPS.DELETE_MESSAGE)}
              />
            )}
            {onTogglePinMessage && (
              <OptionIcon
                source={!isPinned ? icons.pinIcon : icons.unpinnedIcon}
                onClick={() => onTogglePinMessage?.(messageId, !isPinned)}
                title={!isPinned ? t(TOOLTIPS.PIN_MESSAGE) : t(TOOLTIPS.UNPIN_MESSAGE)}
              />
            )}
            {onReplyMessage && (
              <OptionIcon
                source={icons.replyIcon}
                onClick={() => onReplyMessage(true, SIDE_PANEL_THREAD, { messageId })}
                title={t(TOOLTIPS.REPLY_MESSAGE)}
              />
            )}
              {onReadMessage && isMyMessage && (
              <OptionIcon
                source={icons.infoCircleIcon}
                onClick={() => onReadMessage(true, SIDE_PANEL_READERS, { messageId })}
                title={t(TOOLTIPS.READ_MESSAGE)}
              />
            )}
            {onJumpMessage && (isSearch || isPinned) && (
              <OptionIcon
                source={icons.jumpIcon}
                onClick={() => onJumpMessage(jumpId, threadId)}
                title={t(TOOLTIPS.JUMP_MESSAGE)}
              />
            )}
          </div>
        </Tooltip>
      </div>
      {visibleEmojiPicker && (
        <EmojiPickerWrapper
          isMobil={window.innerWidth < MOBILE_DEVICE_WIDTH}
          style={{
            position: 'fixed',
            top: `${pickerPosition.top}px`,
            right: `${pickerPosition.right}px`,
          }}
        >
          <EmojiPicker
            emojiStyle={EmojiStyle.NATIVE}
            lazyLoadEmojis={true}
            reactionsDefaultOpen={true}
            searchDisabled={true}
            height={350}
            width={350}
            onEmojiClick={(e: EmojiClickData) => {
              onReactionMessage?.(messageId, e.emoji, true)
              onToggleVisiblePicker()
            }}
          />
        </EmojiPickerWrapper>
      )}
    </Wrapper>
  )
}

export default Option

export const Wrapper = styled.div.attrs((props: { subsequent: boolean }) => props)`
  display: none;
  background-color: ${colors.white};
  position: absolute;
  top: ${({ subsequent }) => (subsequent ? '-20px' : '0px')};
  right: 30px;
  align-items: center;
  box-sizing: border-box;
  padding: 4px;
  gap: 4px;
  border: 1px solid ${colors.light80};
  box-shadow: 4px 4px 20px 2px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 2;
`

const OptionIcon = styled(CustomIcon)`
  &:hover {
    background-color: ${colors.light50};
    border-radius: 4px;
    flex-grow: 0;
    width: 24px;
    height: 24px;
    display: flex;
  }
`

const EmojiPickerWrapper = styled.div.attrs(
  (props: { positionY: number; isMobil?: boolean }) => props,
)`
  z-index: 999;
  .emoji-picker-react {
    width: ${({ isMobil }) => (isMobil ? '100%' : '350px')};
    height: ${({ isMobil }) => (isMobil ? '456px' : '350px')};
    margin: ${({ isMobil }) => (isMobil ? '0px 0px 10px 0px' : 'initial')};
    padding: ${({ isMobil }) => (isMobil ? '0px 0px 10px 0px' : 'initial')};
  }

  .EmojiPickerReact {
    --epr-emoji-size: 20px;
    --epr-emoji-gap: 3px;
  }

  @media (max-width: ${MOBILE_WIDTH - 400}px) {
    bottom: -22px;
    position: fixed;
    left: 0;
    width: 100%;
    opacity: 1;
    box-shadow: rgba(12, 33, 58, 0.29) 0px -10px 10px;
    border-radius: 4px;
  }

  @media (max-height: ${500}px) {
    position: fixed;
    width: fit-content;
    right: 150px;
    top: 0;
    box-shadow: rgba(12, 33, 58, 0.29) 0px -10px 10px;
    border-radius: 4px;
    height: fit-content;
  }
`
